import { App, Button, Descriptions, Input, Modal, Spin } from "antd"
import { getNextAvailableStatuses as getIsPaidNextAvailableStatuses, getProgramApplyDetailDescriptionItems, useGetProgramBookingAdminByProgramBookingId, useGetProgramForProgramBookingDetailById, useUpdateProgramBookingAdminByUpdateProgramBookingAdminRequest } from "./model"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom";
import { EProgramBookingStatus, programBookingStatusToTextMap, textToProgramBookingStatusMap } from "../../model";
import dayjs from "dayjs";
import { CloseOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { programBookingTicketCountByProgramEventIdAndStatusIn } from "../../../../../../service/programBooking";

interface IProps {
    btnText: string
    selectProgramBookingId: number
    propsProgramId?: number
}
const ProgramBookingDetailModal = ({ btnText, selectProgramBookingId, propsProgramId }: IProps) => {
    const programId = Number(useParams().id)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { message } = App.useApp();
    const [programBooking, setProgramBooking] = useState<IProgramBooking>()
    const { data: programData } = useGetProgramForProgramBookingDetailById(propsProgramId ?? programId)
    const { data, isFetching } = useGetProgramBookingAdminByProgramBookingId(selectProgramBookingId, isOpen)
    const queryClient = useQueryClient();
    const updateProgramBookingAdminByUpdateProgramBookingAdminRequest = useUpdateProgramBookingAdminByUpdateProgramBookingAdminRequest();
    const handleUpdateSend = () => {
        if (!programBooking) return;
        const updateProgramBookingAdminRequest: IUpdateProgramBookingAdminRequest = {
            id: Number(programBooking.id),
            status: textToProgramBookingStatusMap[programBooking.status] ?? programBooking.status,
            note: programBooking.note,
            programEventId: Number(programBooking.programEventId),
            programBookingSelectOptionList: programBooking.programBookingSelectOptionList.map(q => ({
                id: Number(q.id),
                programSelectOptionId: Number(q.programSelectOptionId),
                programSelectOptionItemIdList: q.programSelectOptionItemIdList.map(w => Number(w))
            })),
            programBookingSubjectOptionList: programBooking.programBookingSubjectOptionList.map(q => ({
                id: Number(q.id),
                programSubjectOptionId: Number(q.programSubjectOptionId),
                content: q.content
            }))
        }
        if ((programData?.programEventOutlineList?.flatMap(programEventOutline => programEventOutline.programEventList)?.filter(programEvent => dayjs(programEvent.targetDateTime)?.format('YYYY-MM-DD') === dayjs(programBooking.targetDateTime)?.format('YYYY-MM-DD')).filter(q => q.id === programBooking.programEventId).length ?? 0) <= 0) {
            message.error('예약 시간을 선택해주세요')
        } else {
            updateProgramBookingAdminByUpdateProgramBookingAdminRequest.mutate(updateProgramBookingAdminRequest, {
                onSuccess: res => {
                    if (!res.data.errors) {
                        setIsOpen(false);
                        queryClient.refetchQueries({ queryKey: ['getAllProgramBookingForMemberByMemberId'] });
                        queryClient.refetchQueries({ queryKey: ['getAllProgramBookingAdminByProgramId', programId] })
                    } else {
                        message.error('에러가 발생했습니다 개발팀에 문의해주세요.  ' + res.data.errors[0].message)
                    }
                }
            })
        }
    }
    const onClickSaveBtn = async () => {
        if (!programBooking || !data || !programData) return message.error('선택된 예역내역이 없습니다.');
        const mappedDataStatus = textToProgramBookingStatusMap[data.status] ?? data.status;
        const mappedBookingStatus = textToProgramBookingStatusMap[programBooking.status] ?? programBooking.status;
        const shouldCheckCapacity = (programData.isPaid && mappedDataStatus === EProgramBookingStatus.PENDING && mappedBookingStatus === EProgramBookingStatus.REQUEST_PAID) || (programData.isPaid && mappedDataStatus === EProgramBookingStatus.PENDING && mappedBookingStatus === EProgramBookingStatus.COMPLETE) || (!programData.isPaid && mappedDataStatus === EProgramBookingStatus.PENDING && mappedBookingStatus === EProgramBookingStatus.COMPLETE);
        if (shouldCheckCapacity) {
            const programEventList = programData?.programEventOutlineList.flatMap(e => e.programEventList);
            const targetProgramEvent = programEventList?.find(e => Number(e.id) === Number(programBooking.programEventId));
            if (!targetProgramEvent) return;
            const res = await programBookingTicketCountByProgramEventIdAndStatusIn(Number(programBooking.programEventId), [EProgramBookingStatus.REQUEST_PAID, EProgramBookingStatus.COMPLETE, EProgramBookingStatus.REQUEST_CANCEL, EProgramBookingStatus.ABSENT, EProgramBookingStatus.CHECK_IN]);
            const bookingCnt = programBooking.programBookingTicketList?.reduce((acc, cur) => acc + cur.count, 0) ?? 0;
            const remaining = (targetProgramEvent.maxBookingCnt ?? 0) - res.data - bookingCnt;
            const proceed = remaining < 0
                ? window.confirm('모집 인원을 초과했습니다. 그래도 진행하시겠습니까?')
                : true;

            if (proceed) {
                handleUpdateSend();
            } else {
                return;
            }
        } else {
            handleUpdateSend();
        }
    }
    useEffect(() => {
        if (!data) return;
        setProgramBooking(data)
    }, [isFetching, isOpen])

    return <>
        <div style={{ color: 'var(--ant-color-link)', cursor: 'pointer' }} onClick={e => setIsOpen(true)}>{btnText}</div>

        <Modal width={'626px'} open={isOpen} onOk={() => setIsOpen?.(false)} onCancel={() => setIsOpen?.(false)} footer={null} closeIcon={null}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '20px', lineHeight: '28px', paddingBottom: '24px' }}>
                <div>
                    예약 상세
                </div>
                <div style={{ cursor: 'pointer' }} onClick={() => setIsOpen(false)}>
                    <CloseOutlined style={{ color: "#00000073" }} />
                </div>
            </div>
            {isFetching
                ? <Spin />
                : programBooking && <>
                    <Descriptions styles={{ label: { minWidth: '108px', fontWeight: 600, fontSize: '14px', lineHeight: '22px', color: '#000000E0', padding: '21px 8px' } }} column={1} bordered items={programData && programBooking && getProgramApplyDetailDescriptionItems(programData, programBooking, setProgramBooking, getIsPaidNextAvailableStatuses(programBooking?.status as EProgramBookingStatus).map(q => ({ label: programBookingStatusToTextMap[q] ?? q, value: textToProgramBookingStatusMap[q] ?? q })))} />
                    <div style={{ display: 'flex', alignItems: 'center', paddingBlock: '24px' }}>
                        <div style={{ flex: 1, fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#000000E0', }}>
                            비고
                        </div>
                        <Input.TextArea style={{ flex: 8 }} value={programBooking?.note} onChange={(q) =>
                            setProgramBooking((prev) => (prev && {
                                ...prev,
                                note: q.target.value,
                            }))
                        } />
                    </div>

                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'end' }}>
                        <Button type='primary' loading={updateProgramBookingAdminByUpdateProgramBookingAdminRequest.isPending} onClick={onClickSaveBtn} >저장</Button>
                    </div>
                </>}
        </Modal>
    </>
}


export { ProgramBookingDetailModal }
